import { useState, useEffect } from 'react';
import useExternalSurvey from './hooks/useExternalSurvey';
import Loading from '@/src/Common/components/Loading';

function useIframeResize(initialHeight = '100vh') {
    const [height, setHeight] = useState(initialHeight);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const data = event.data;
            const h = data?.height ?? data?.frameHeight ?? data?.size?.height;
            if (typeof h === 'number' && h > 0) {
                setHeight(`${h}px`);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return height;
}

interface ExternalSurveyProps {
    galleryInfo: GalleryObject | null;
    destinationIdentifier: string;
    onLoad: () => void;
}

export default function ExternalSurvey({
    galleryInfo,
    destinationIdentifier,
    onLoad = () => {},
}: ExternalSurveyProps) {
    const {
        hasSurveyBeenCompleted,
        checkSurveyCompletion,
        surveyToken,
        hasTokenBeenClaimed,
    } = useExternalSurvey(galleryInfo);
    const [isLoading, setIsLoading] = useState(true);
    const iframeHeight = useIframeResize('100vh');

    useEffect(() => {
        setIsLoading(true);
        checkSurveyCompletion(destinationIdentifier);
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationIdentifier]);

    useEffect(() => {
        if (hasTokenBeenClaimed) {
            checkSurveyCompletion(destinationIdentifier);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasTokenBeenClaimed]);

    const showSurvey =
        !isLoading && !hasSurveyBeenCompleted && !!galleryInfo?.surveyUrl;

    useEffect(() => {
        if (!isLoading && !showSurvey) {
            onLoad();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, showSurvey]);

    if (isLoading) {
        return <Loading isLoading={isLoading} />;
    }

    if (!showSurvey) {
        return null;
    }

    const surveyUrl = `${galleryInfo?.surveyUrl}?token=${surveyToken}&activationId=${galleryInfo?.activationId}&destinationIdentifier=${destinationIdentifier}&utm_source=${surveyToken}&utm_medium=${galleryInfo?.activationId}&utm_term=${destinationIdentifier}`;

    return (
        <iframe
            className="w-full border-0"
            style={{ minHeight: '100vh', height: iframeHeight }}
            src={surveyUrl}
            title="Survey"
        />
    );
}
